/* eslint-disable @typescript-eslint/no-unused-vars */
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { z } from 'zod';

import { queryClient } from '@/App';
import { RequirementCategoryEnum } from '@/constants/enums/RequirementCategoryEnum';
import { RequirementClassEnum } from '@/constants/enums/RequirementClassEnum';
import {
	EducationalRequirementsSchema,
	JobTitleFormSchema,
	OtherRequirementsSchema,
	ProfessionalRequirementsSchema,
} from '@/constants/formSchema/JobtitleFormSchema';
import { msalInstance } from '@/main';
import api from '@/services/apiSgft';
import { actualUserAtom } from '@/state/ActualUser.atom';
import { Option } from '@/types/Filter';
import { GroupedJobTitle, JobTitle } from '@/types/JobTitle';
import { GeneralInformationJobTitleCreationData } from '@/types/JobTitleCreationData';

import { Button } from '../atoms/Button';
import ControlledSimpleSelect from '../atoms/ControlledSimpleSelect';
import Modal from '../atoms/Modal';
import SearchSelect from '../atoms/SearchSelect';
import Spinner from '../atoms/Spinner';
import UncontrolledInputText from '../atoms/UncontrolledInputText';
import { RegisterJobTitle } from './RegisterJobTitle';

interface RegisterGeneralInformationJobTitleModalProps {
	isOpen: boolean;
	onClose: () => void;
	data?: GroupedJobTitle;
	type: 'create' | 'edit';
}

const getRequirements = (jobTitle: JobTitle) => {
	const filterRequirements = (
		requirementClass: RequirementClassEnum,
		category: RequirementCategoryEnum,
	) =>
		jobTitle.jobTitleRequirements
			.filter(
				(it) =>
					it.requirementClass.name === requirementClass &&
					it.requirement.requirementCategory.name === category,
			)
			.map((it) => it.requirement.name);

	const categories = {
		educationalRequirements: [
			RequirementCategoryEnum.undergraduate,
			RequirementCategoryEnum.levelOfEducation,
			RequirementCategoryEnum.postgraduate,
			RequirementCategoryEnum.technicalEducation,
		],
		professionalRequirements: [
			RequirementCategoryEnum.softwares,
			RequirementCategoryEnum.activeProfessionalRecords,
			RequirementCategoryEnum.knowledge,
			RequirementCategoryEnum.otherCourses,
			RequirementCategoryEnum.certifications,
		],
		otherRequirements: [
			RequirementCategoryEnum.languages,
			RequirementCategoryEnum.other,
		],
	};

	const categoryKeys = Object.keys(RequirementCategoryEnum).reduce(
		(acc, key) => {
			const value =
				RequirementCategoryEnum[key as keyof typeof RequirementCategoryEnum];
			acc[value] = key;
			return acc;
		},
		{} as Record<string, string>,
	);

	const buildRequirements = (requirementClass: RequirementClassEnum) => {
		const result: Record<string, Record<string, string[]>> = {};

		Object.entries(categories).forEach(([key, subcategories]) => {
			result[key] = subcategories.reduce(
				(acc, subcategory) => {
					const subcategoryKey = categoryKeys[subcategory] || subcategory;
					acc[subcategoryKey] = filterRequirements(
						requirementClass,
						subcategory,
					);
					return acc;
				},
				{} as Record<string, string[]>,
			);
		});

		return result;
	};

	return {
		mandatory: buildRequirements(RequirementClassEnum.MANDATORY),
		optional: buildRequirements(RequirementClassEnum.OPTIONAL),
	};
};

export const RegisterGeneralInformationJobTitleModal: React.FC<
	RegisterGeneralInformationJobTitleModalProps
> = ({ isOpen, onClose, data, type }) => {
	const actualUser = useRecoilValue(actualUserAtom);
	const [isSubmiting, setIsSubmiting] = useState(false);
	const [cboSearch, setCboSearch] = useState('');
	const methods = useForm<z.infer<typeof JobTitleFormSchema>>({
		...(type === 'edit' && {
			defaultValues: {
				company: data?.company.name ?? '',
				title: data?.title ?? '',
				workStations:
					data?.workStations.map(
						(workStation) => workStation.workStation.name,
					) ?? [],
				summary: data?.summary ?? '',
				observations: data?.observations ?? '',
				levels:
					data?.jobTitles.map((jobTitle) => ({
						id: jobTitle.id,
						level: jobTitle.seniorityLevel.name,
						experience: jobTitle.experience,
						objective: jobTitle.objective,
						rolesAndResponsibilities: jobTitle.responsibilities,
						skillsAndCompetencies: jobTitle.skillsAndCompetencies,
						requirements: getRequirements(jobTitle),
					})) ?? [],
				managements:
					data?.managements?.map((management) => management.name) ?? [],
				boards:
					data?.managements.map((management) => management.board.name) ?? [],
				cbo: data?.cbo,
			},
		}),
	});
	const parseRequirementsByLevel = (
		data: z.infer<typeof JobTitleFormSchema>['levels'],
	) => {
		const requirements = {
			mandatory: {
				educationalRequirements: [] as z.infer<
					typeof EducationalRequirementsSchema
				>[],
				professionalRequirements: [] as z.infer<
					typeof ProfessionalRequirementsSchema
				>[],
				otherRequirements: [] as z.infer<typeof OtherRequirementsSchema>[],
			},
			optional: {
				educationalRequirements: [] as z.infer<
					typeof EducationalRequirementsSchema
				>[],
				professionalRequirements: [] as z.infer<
					typeof ProfessionalRequirementsSchema
				>[],
				otherRequirements: [] as z.infer<typeof OtherRequirementsSchema>[],
			},
		};
		for (const level of data) {
			level.requirements.mandatory.educationalRequirements.level = level.level;
			level.requirements.mandatory.professionalRequirements.level = level.level;
			level.requirements.mandatory.otherRequirements.level = level.level;
			level.requirements.optional.educationalRequirements.level = level.level;
			level.requirements.optional.professionalRequirements.level = level.level;
			level.requirements.optional.otherRequirements.level = level.level;
		}
		for (const level of data) {
			requirements.mandatory.educationalRequirements.push(
				level.requirements.mandatory.educationalRequirements,
			);
			requirements.mandatory.professionalRequirements.push(
				level.requirements.mandatory.professionalRequirements,
			);
			requirements.mandatory.otherRequirements.push(
				level.requirements.mandatory.otherRequirements,
			);
			requirements.optional.educationalRequirements.push(
				level.requirements.optional.educationalRequirements,
			);
			requirements.optional.professionalRequirements.push(
				level.requirements.optional.professionalRequirements,
			);
			requirements.optional.otherRequirements.push(
				level.requirements.optional.otherRequirements,
			);
		}
		return requirements;
	};

	const onSubmit = async (formData: z.infer<typeof JobTitleFormSchema>) => {
		try {
			setIsSubmiting(true);
			const { boards, managements, ...remainingData } = formData;

			const levels = methods.getValues('levels'); // TODO - understand why it's not working with formData.levels
			const userEmail =
				actualUser.email || msalInstance.getActiveAccount()?.username || '';
			const submitData = {
				...remainingData,
				requester: userEmail,
				revisor: userEmail,
				approver: userEmail,
				levels: levels.map(({ requirements, ...levelData }) => ({
					...levelData,
					// level: level.level,
				})),
				requirements: parseRequirementsByLevel(levels),
				boards,
				managements,
				workStations: formData.workStations.map((workStation) => {
					const previousWorkStation = data?.workStations.find(
						(ws) => ws.workStation.name === workStation,
					);

					return {
						workStation,
						gheCode: previousWorkStation?.gheCode ?? null,
						epi: previousWorkStation?.epi ?? null,
						risks: previousWorkStation?.risks ?? null,
					};
				}),
			};
			if (type === 'create') {
				const response = await api.post('/job-title', submitData);
				toast.success(response.data, {
					theme: 'colored',
					toastId: 'success',
				});
			} else if (type === 'edit') {
				const response = await api.put(
					`/job-title/update/${data?.id}`,
					submitData,
				);

				if (response.data?.passedWithErrors)
					toast.warn(response.data.message, {
						theme: 'colored',
						toastId: 'warning',
					});
				else if (response.data?.message)
					toast.success(response.data.message, {
						theme: 'colored',
						toastId: 'success',
					});
				else
					toast.success(response.data, {
						theme: 'colored',
						toastId: 'success',
					});
			}
			queryClient.invalidateQueries(['jobTitle']);
			queryClient.invalidateQueries(['jobTitlesCount']);
			closeAndClear();
		} catch (error: any) {
			let errorMessage = `Erro ao ${type === 'create' ? 'criar' : type === 'edit' ? 'editar' : 'excluir'} cargo, tente novamente mais tarde!`;
			if (error.response.status !== 500) {
				errorMessage = error.response.data.message;
			}
			toast.error(errorMessage, {
				theme: 'colored',
				toastId: 'error',
			});
			throw error;
		} finally {
			setIsSubmiting(false);
		}
	};

	const closeAndClear = () => {
		methods.reset();
		onClose();
	};

	const getCreationData = async () => {
		try {
			const response = await api.get<GeneralInformationJobTitleCreationData>(
				'/job-title/general-information-job-titles-creation-data',
			);
			return response.data;
		} catch (e) {
			toast.error('Erro ao carregar os dados!', {
				theme: 'colored',
				toastId: 'error',
			});
		}
	};

	const getCbo = async () => {
		try {
			const response = await api.get('/job-title/cbo', {
				params: {
					search: cboSearch,
					size: 10,
					page: 1,
				},
			});
			return response.data;
		} catch (_e) {
			toast.error('Erro ao carregar os dados!', {
				theme: 'colored',
				toastId: 'error',
			});
		}
	};

	const { data: creationData, isLoading: isLoadingCompanies } = useQuery<
		GeneralInformationJobTitleCreationData | undefined
	>(['get-job-title-creation-data'], getCreationData);

	const { data: cboData } = useQuery(['cbo', cboSearch], getCbo);

	const companiesOptions =
		creationData?.companies.map((company) => ({
			value: company.name,
			label: company.name,
		})) ?? [];
	const companyWatch = methods.watch('company');
	const boardsWatch = methods.watch('boards');
	const managementsWatch = methods.watch('managements');

	const boardsOptions =
		creationData?.boards
			.filter((board) => board.company.name === companyWatch)
			.map((board) => ({
				value: board.name,
				label: board.name,
			})) ?? [];

	useEffect(() => {
		const availableBoards = new Set(boardsOptions.map((board) => board.value));
		const selectedBoards = boardsWatch ?? [];
		const filteredBoards = selectedBoards.filter((board) =>
			availableBoards.has(board),
		);

		if (filteredBoards.length !== selectedBoards.length) {
			methods.setValue('boards', filteredBoards, {
				shouldValidate: true,
				shouldDirty: true,
			});
		}
	}, [boardsOptions, boardsWatch, methods]);

	const managementsOptions =
		creationData?.managements
			.filter(
				(management) =>
					boardsWatch?.includes(management.board.name) &&
					management.board.company.name === companyWatch,
			)
			.map((management) => ({
				value: management.name,
				label: management.name,
			})) ?? [];

	useEffect(() => {
		const availableManagements = new Set(
			managementsOptions.map((management) => management.value),
		);
		const selectedManagements = managementsWatch ?? [];
		const filteredManagements = selectedManagements.filter((management) =>
			availableManagements.has(management),
		);

		if (filteredManagements.length !== selectedManagements.length) {
			methods.setValue('managements', filteredManagements, {
				shouldValidate: true,
				shouldDirty: true,
			});
		}
	}, [managementsOptions, managementsWatch, methods]);

	const workStationsOptions =
		creationData?.workStations.map((workStation) => ({
			value: workStation.name,
			label: workStation.name,
		})) ?? [];

	const cbosOptions: Option[] =
		cboData?.map((cbo: { name: string }) => ({
			value: cbo.name,
			label: cbo.name,
		})) ?? [];

	const cboMergedOptions = data?.cbo
		? cbosOptions.some((cbo) => cbo.value === data.cbo)
			? cbosOptions
			: [...cbosOptions, { value: data.cbo, label: data.cbo }]
		: cbosOptions;

	return (
		<Modal
			isOpen={isOpen}
			onClose={closeAndClear}
			title={`${type === 'create' ? 'Criar novo cargo' : 'Editar cargo'}`}
			className="w-[64rem] max-w-[85vw]"
		>
			<FormProvider {...methods}>
				<form
					className="space-between flex h-[90%] flex-col px-5 py-2"
					onSubmit={methods.handleSubmit(onSubmit)}
				>
					{isLoadingCompanies ? (
						<div className="flex h-[19rem] w-full justify-center">
							<Spinner />
						</div>
					) : (
						<div className="w-full">
							<div className="mb-4 flex w-full justify-between">
								<UncontrolledInputText
									title="Título do cargo"
									required
									width="61rem"
									{...methods.register('title')}
									upperCase
								/>
							</div>
							<div className="mb-4 flex w-full gap-2">
								<ControlledSimpleSelect
									title={'Empresa'}
									required
									control={methods.control}
									name={`company`}
									width="30rem"
									values={companiesOptions}
								/>
								<SearchSelect
									{...methods.register('cbo')}
									control={methods.control}
									title="CBO"
									options={cboMergedOptions}
									required
									className="w-[30rem]"
									setSearch={(e) => setCboSearch(e)}
								/>
							</div>
							{companyWatch && (
								<div className="mb-4 flex w-full gap-2">
									<ControlledSimpleSelect
										title={'Diretorias'}
										required
										control={methods.control}
										name={`boards`}
										width="30rem"
										values={boardsOptions}
										multiple
									/>
									<ControlledSimpleSelect
										title={'Gerências'}
										required
										control={methods.control}
										name={'managements'}
										width="30rem"
										values={managementsOptions}
										multiple
									/>
								</div>
							)}
							<div className="mb-4 flex w-full justify-between">
								<ControlledSimpleSelect
									title={'Local de Trabalho'}
									required
									control={methods.control}
									name={`workStations`}
									width="61rem"
									values={workStationsOptions}
									multiple
								/>
							</div>
							<div className="mb-4 flex w-full justify-between">
								<UncontrolledInputText
									title="Resumo do cargo"
									width="61rem"
									{...methods.register('summary')}
								/>
							</div>
							<div className="mb-4 flex w-full justify-between">
								<UncontrolledInputText
									title="Observação para Ordem de Serviço"
									width="61rem"
									{...methods.register('observations')}
								/>
							</div>
							{type === 'create' && (
								<div className="flex w-[90%] justify-between">
									<div className="2 flex items-center gap-4 text-[12px] text-[#4A4A4A]">
										<label className="flex items-center">
											<input
												type="radio"
												title="Criar cargo somente para a Estrutura Organizacional"
												value="structureCreation"
												defaultChecked
												{...methods.register('creationType')}
											/>
											<span className="ml-2">
												Criar cargo somente para a Estrutura Organizacional
											</span>
										</label>
										<label className="flex items-center">
											<input
												type="radio"
												value="sendToQsms"
												{...methods.register('creationType')}
											/>
											<span className="ml-2">
												Enviar cargo para avaliação de GHE por QSMS
											</span>
										</label>
									</div>
								</div>
							)}
							<RegisterJobTitle methods={methods} creationData={creationData} />
							<div className="my-2 mt-6 flex w-full justify-between">
								<Button
									type="button"
									className="h-8 min-w-36 border-2 border-[#193CB9] bg-white text-[#193CB9] hover:bg-[#e9eaf1]"
									onClick={closeAndClear}
								>
									Cancelar
								</Button>
								<Button
									type="submit"
									className=" h-8 min-w-36"
									isLoading={isSubmiting}
								>
									Salvar
								</Button>
							</div>
						</div>
					)}
				</form>
			</FormProvider>
		</Modal>
	);
};
